//@flow
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import theme from './ZoneMapsSidebar.scss';

import { sidebarFactory } from 'common/components/core/sidebar';
import { listCardFactory } from 'common/components/core/card/ListCard';
import Button, {
    ButtonTypes,
    ButtonWithPopover
} from 'common/components/core/button';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { EditableInputField } from 'common/components/core/common/CustomEditableField';
import { List, Map } from 'immutable';
import ZoneMapGeometryCard from 'common/components/zoneMap/ZoneMapGeometryCard';
import ZoneMapFolderCard from 'common/components/zoneMap/ZoneMapFolderCard';
import SidebarFooter from './SidebarFooter';
import VerifyButton from 'common/entity/generic/VerifyButton';
import { withProps, compose, withPropsOnChange } from 'recompose';
import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';

const BackButton = ({ theme, onBack }) => {
    return (
        <Button
            theme={theme}
            className={theme.backBtn}
            icon="nav-arrow-left"
            buttonType={ButtonTypes.Icon}
            onClick={onBack}
        />
    );
};

const RightIcons = ({
    t,
    isMapVerified,
    onVerify,
    isVerifyDisabled,
    isVerifyHidden,
    namedZones,
    toggleLabels
}) => {
    let popOver;

    if (isVerifyDisabled) {
        popOver = t('zoneMap.noVerifyAccess');
    } else {
        popOver = t(
            isMapVerified ? 'zoneMap.unverifyAll' : 'zoneMap.verifyAll'
        ).concat('?');
    }
    return (
        <div className={theme.rightIcons}>
            <ButtonWithPopover
                theme={theme}
                ripple={false}
                className={theme.viewLabelsBtn}
                icon={namedZones ? 'label-visible' : 'label-hidden'}
                onClick={toggleLabels}
                invertPopover
                popOverContent={t('zoneMap.toggleLabels', {
                    action: namedZones ? t('zoneMap.hide') : t('zoneMap.show')
                })}
                place={PopoverPlaceEnum.Above}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
                buttonType={ButtonTypes.Icon}
            />
            {!isVerifyHidden ? (
                <VerifyButton
                    disabled={isVerifyDisabled}
                    theme={theme}
                    isVerified={isMapVerified}
                    onVerify={onVerify}
                    noLabel
                    popOver={popOver}
                />
            ) : null}
        </div>
    );
};

const TranslatedRightIcons = translate('entityManagement')(RightIcons);
const TitleCard = listCardFactory({
    LeftIcon: BackButton,
    RightIcon: TranslatedRightIcons,
    Title: EditableInputField
});

const GeometryCard = withProps(
    ({
        handleZoneVisibilityToggle,
        handleEntityCardClick,
        source,
        isSelected,
        hoveredGeometryIds,
        selectedGroupId
    }) => {
        return {
            geometry: source,
            onClick: handleEntityCardClick,
            onHide: handleZoneVisibilityToggle,
            isSelected:
                isSelected || hoveredGeometryIds.includes(source.id.getId()),
            isExpanded:
                selectedGroupId && source.directoryId === selectedGroupId,
            isHidden: source.isHidden()
        };
    }
)(ZoneMapGeometryCard);

const FolderCard = withProps(
    ({
        source,
        subsections,
        handleFolderCardClick,
        handleFolderVisibilityToggle,
        selectedGroupId,
        handleFolderDelete
    }) => {
        return {
            folder: source,
            geometriesIncluded: subsections,
            isExpanded: selectedGroupId === source.id,
            isHidden: source.isHidden(),
            onClick: handleFolderCardClick,
            onHide: handleFolderVisibilityToggle,
            onDelete: handleFolderDelete
        };
    }
)(ZoneMapFolderCard);

const Sidebar = sidebarFactory({
    Title: compose(
        translate('entityManagement'),
        withProps(
            ({ t, mapName, handleMapNameSave, handleMapVerifyToggle }) => {
                return {
                    isEditable: true,
                    isEditingEnabled: true,
                    placeholder: t('zoneMap.untitledMap'),
                    value: mapName,
                    onSave: handleMapNameSave,
                    onVerify: handleMapVerifyToggle
                };
            }
        )
    )(TitleCard),
    UngroupedSection: GeometryCard,
    GroupedSection: FolderCard,
    SubSection: GeometryCard,
    Footer: withProps(props => {
        const { handleAddFolder, handleZoneMapSave } = props;
        return {
            onAdd: handleAddFolder,
            onSave: handleZoneMapSave
        };
    })(SidebarFooter),
    options: { draggable: true }
});

class ZoneMapsSidebar extends PureComponent {
    state = {
        hoveredDragIndex: null
    };

    onDragUpdate = update => {
        const { source, destination } = update || {};
        if (destination && source) {
            const { index: destIndex, droppableId } = destination;
            const {
                index: sourceIndex,
                droppableId: sourceDroppableId
            } = source;

            let hoveredDragIndex = null;
            if (
                droppableId === 'ungrouped' &&
                sourceDroppableId === 'ungrouped'
            ) {
                hoveredDragIndex =
                    sourceIndex >= destIndex ? destIndex - 1 : destIndex;
            } else if (droppableId === 'ungrouped') {
                hoveredDragIndex = destIndex - 1;
            } else {
                hoveredDragIndex = droppableId;
            }

            this.setState({ hoveredDragIndex });
        }
    };

    onDragEnd = result => {
        const {
            handleReorderZone,
            folders,
            geometries,
            ungroupedGeometries,
            groupedGeometryMap
        } = this.props;

        if (result && result.destination) {
            const {
                source: { droppableId: sourceDroppableId },
                draggableId: id,
                destination: { droppableId, index }
            } = result;
            const geometry = geometries.find(
                geometry => geometry.id.getId() === id
            );
            if (droppableId === 'ungrouped') {
                let offset = geometries.size - ungroupedGeometries.size;
                let reorderProps = {};
                if (sourceDroppableId !== 'ungrouped') {
                    offset = offset - 1;
                    reorderProps = { ...reorderProps, removeFolder: true };
                }

                handleReorderZone({
                    ...reorderProps,
                    geometry,
                    index: index + offset
                });
            } else {
                let found = false;
                let offset = 0;
                folders.forEach(folder => {
                    if (!found) {
                        if (folder.id === droppableId) {
                            found = true;
                        } else {
                            const groupedGeometries = groupedGeometryMap.get(
                                folder.id
                            );
                            offset =
                                offset +
                                (groupedGeometries
                                    ? groupedGeometries.size
                                    : 0);
                        }
                    }
                });
                handleReorderZone({
                    geometry,
                    folderId: droppableId,
                    index: index + offset
                });
            }
        }
        this.setState({ hoveredDragIndex: null });
    };

    render() {
        const {
            mapName,
            sections,
            isMapVerified,
            onVerify,
            isVerifyDisabled,
            isVerifyHidden,
            namedZones,
            toggleLabels,
            isMapModified,
            handleZoneMapSave,
            handleAddFolder,
            handleGeometryCardHover,
            resetHoveredGeometries,
            handleZoneDelete,
            ungroupZone,
            handleEntityCardClick,
            handleZoneVisibilityToggle,
            handleZoneVerifyToggle,
            handleFolderNameUpdate,
            handleFolderVerifyToggle,
            handleFolderCardHover,
            handleFolderCardBlur,
            handleFolderCardClick,
            handleFolderVisibilityToggle,
            handleFolderDelete,
            handleMapNameSave,
            onBack,
            handleMapVerifyToggle,
            selectedZoneId,
            selectedGroupId,
            hoveredGeometryIds
        } = this.props;
        const { hoveredDragIndex } = this.state;
        return (
            <Sidebar
                hoveredDragIndex={hoveredDragIndex}
                selectedSectionId={selectedZoneId || selectedGroupId}
                selectedGroupId={selectedGroupId}
                handleMapNameSave={handleMapNameSave}
                mapName={mapName}
                theme={theme}
                sections={sections}
                isMapVerified={isMapVerified}
                onVerify={onVerify}
                isVerifyDisabled={isVerifyDisabled}
                isVerifyHidden={isVerifyHidden}
                namedZones={namedZones}
                toggleLabels={toggleLabels}
                isMapModified={isMapModified}
                handleZoneMapSave={handleZoneMapSave}
                handleAddFolder={handleAddFolder}
                handleGeometryCardHover={handleGeometryCardHover}
                resetHoveredGeometries={resetHoveredGeometries}
                handleZoneDelete={handleZoneDelete}
                ungroupZone={ungroupZone}
                handleEntityCardClick={handleEntityCardClick}
                handleZoneVisibilityToggle={handleZoneVisibilityToggle}
                handleZoneVerifyToggle={handleZoneVerifyToggle}
                handleFolderNameUpdate={handleFolderNameUpdate}
                handleFolderVerifyToggle={handleFolderVerifyToggle}
                handleFolderCardHover={handleFolderCardHover}
                handleFolderCardBlur={handleFolderCardBlur}
                handleFolderCardClick={handleFolderCardClick}
                handleFolderVisibilityToggle={handleFolderVisibilityToggle}
                handleFolderDelete={handleFolderDelete}
                onDragUpdate={this.onDragUpdate}
                onDragEnd={this.onDragEnd}
                onBack={onBack}
                handleMapVerifyToggle={handleMapVerifyToggle}
                hoveredGeometryIds={hoveredGeometryIds}
            />
        );
    }
}

const parseSections = (geometries, folders) => {
    let geometryMap = { grouped: Map(), ungrouped: List() };
    let sections = [];
    geometries.forEach(geometry => {
        if (
            geometry.getInternalStatus() !==
                ZoneMapGeometryStatusEnum.Inactive &&
            geometry.getType() !== ZoneMapGeometryTypeEnum.Point &&
            geometry.getInternalStatus() !== ZoneMapGeometryStatusEnum.Search
        ) {
            const { directoryId } = geometry;
            if (directoryId) {
                const { grouped: existingGrouped } = geometryMap;
                const groupedGeometries = existingGrouped.get(directoryId);
                const grouped = groupedGeometries
                    ? groupedGeometries.push(geometry)
                    : List().push(geometry);
                const foldersMap = existingGrouped.set(directoryId, grouped);
                geometryMap = { ...geometryMap, grouped: foldersMap };
            } else {
                const ungrouped = geometryMap.ungrouped.push(geometry);
                geometryMap = { ...geometryMap, ungrouped };
            }
        }
    });

    folders.forEach(folder => {
        const { id } = folder;
        const { grouped: existingGrouped } = geometryMap;
        if (!existingGrouped.has(id)) {
            sections = [...sections, { id, section: folder, isGrouped: true }];
        } else {
            sections = [
                ...sections,
                {
                    id,
                    section: folder,
                    subsections: existingGrouped.get(id),
                    isGrouped: true
                }
            ];
        }
    });
    return {
        grouped: sections,
        ungrouped: geometryMap.ungrouped,
        groupedMap: geometryMap.grouped
    };
};

const ZoneMapsSidebarWithSections = withPropsOnChange(
    ['geometries', 'folders'],
    ({ geometries, folders }) => {
        const { grouped, ungrouped, groupedMap } = parseSections(
            geometries,
            folders
        );
        const sections = [...grouped, ...ungrouped];
        return {
            sections,
            ungroupedGeometries: ungrouped,
            groupedGeometryMap: groupedMap
        };
    }
)(ZoneMapsSidebar);

export default translate('entityManagement')(ZoneMapsSidebarWithSections);
